import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getFacilityKeyForPortal, getPatientKey } from "@/store/profile";
import { useTaskStore } from "@/store/task";
import { ProfileInfo } from "@/components/profile/ProfileInfo";
import taskRequest from "@/api/task-request";
import { markIntakeStepDone } from "@/store/intakeProgress";

/**
 * Demographics step: same component as the Patient Info tab (no other profile tabs),
 * with Back and Next navigation.
 *
 * When the user saves and continues, we find or create a case history (same as Vue
 * PatientInfo) and set it in the task store so complaint/health history flows have
 * a case history id for saving.
 */
export function DemographicsPage() {
  const navigate = useNavigate();
  const caseHistoryId = useTaskStore((s) => s.caseHistory?.id);
  const setCurrentCaseHistoryId = useTaskStore((s) => s.setCurrentCaseHistoryId);
  const handleSaveAndContinue = async () => {
    const facilityKey = getFacilityKeyForPortal();
    const patientKey = getPatientKey();
    if (facilityKey && patientKey && (caseHistoryId == null || caseHistoryId === 0)) {
      try {
        const result = await taskRequest.request.findIncompleteCaseHistory(
          facilityKey,
          patientKey
        );
        let id: number | undefined =
          typeof result?.data === "number"
            ? result.data
            : (result?.data as { id?: number } | undefined)?.id;
        if (id == null || id === 0) {
          const insert = await taskRequest.request.insertCaseHistory(
            facilityKey,
            patientKey,
            { aa: "false" }
          );
          id =
            typeof insert?.data === "number"
              ? insert.data
              : (insert?.data as { id?: number } | undefined)?.id;
        }
        if (id != null && id > 0) {
          setCurrentCaseHistoryId(id);
        } else {
          toast.error("We couldn't set up your intake record. Please try again.");
          return;
        }
      } catch (e) {
        console.error("Demographics: find/create case history failed:", e);
        toast.error("Something went wrong saving your information. Please try again.");
        return;
      }
    }
    markIntakeStepDone("demographics");
    navigate("/complaint/communication-preferences");
  };

  return (
    <div className="wizard-page flex w-full min-h-0 flex-col">
      <ProfileInfo
        mode="wizard"
        onBack={() => navigate("/")}
        onSaveAndContinue={handleSaveAndContinue}
      />
    </div>
  );
}
